import type { Edge, Node } from '@xyflow/react'

/**
 * One connected universe at a time.
 *
 * A strategy runs against one sample, so exactly one universe may feed the
 * graph. Several universe nodes may SIT on the canvas: keeping "Small caps $1
 * to $5" parked beside "Sub $10 gappers" and rewiring to switch between them is
 * a good way to work, and the count is only ambiguous once two are wired in.
 *
 * Enforced twice, on purpose. Connecting one releases the others, so the
 * ambiguous state is unreachable by normal wiring (the same last-write-wins
 * treatment `priceData.linkedChartIndex` already gives chart collisions). The
 * validator catches graphs that arrive some other way: an import, a paste, or
 * an AI-generated graph.
 */

/** Universe nodes that currently feed something. */
export function connectedUniverseIds(
  nodes: Node[] | undefined,
  edges: Edge[] | undefined,
): string[] {
  if (!nodes || !edges) return []
  const universeIds = new Set(nodes.filter((n) => n.type === 'universe').map((n) => n.id))
  if (universeIds.size === 0) return []

  const connected = new Set<string>()
  for (const edge of edges) {
    // Proxy edges are the collapsed-subnet projection of a real edge, so
    // counting them would double-count one connection.
    if ((edge.data as { proxy?: boolean } | undefined)?.proxy === true) continue
    if (universeIds.has(edge.source)) connected.add(edge.source)
  }
  return [...connected]
}

/**
 * Drop every universe's outgoing edges except `keepId`'s.
 *
 * Returns the input array unchanged when there is nothing to release, so the
 * common case does not churn the edge identity and re-render the canvas.
 */
export function dropOtherUniverseEdges(
  nodes: Node[],
  edges: Edge[],
  keepId: string | null | undefined,
): Edge[] {
  if (!keepId) return edges
  const universeIds = new Set(nodes.filter((n) => n.type === 'universe').map((n) => n.id))
  if (!universeIds.has(keepId)) return edges

  const doomed = edges.filter((e) => universeIds.has(e.source) && e.source !== keepId)
  if (doomed.length === 0) return edges
  return edges.filter((e) => !(universeIds.has(e.source) && e.source !== keepId))
}
